import { datadogLogs } from "@datadog/browser-logs";
import { formatDuration, intervalToDuration } from "date-fns";

import { APPOINTMENT_PAGE_REDESIGN } from "constants/feature-flags";
import type {
  DeepGramDialogEntry,
  DeepgramInput
} from "services/types/ambient-scribe.type";
import { viteEnv } from "utils/vite-env.util";

import {
  AMBIENT_SCRIBE_STATUS_AWAITING_UPLOAD,
  AMBIENT_SCRIBE_STATUS_CANCELLED,
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_FAILED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RECORDING,
  AMBIENT_SCRIBE_STATUS_RETRYING,
  AMBIENT_SCRIBE_STATUS_TRANSCRIBED,
  AMBIENT_SCRIBE_STATUS_UPLOADED,
  AMBIENT_SCRIBE_STATUS_UPLOADING,
  AMBIENT_SCRIBE_STATUS_UPLOAD_FAILED
} from "./medical-scribe-constants";
import type { RecordingData } from "./medical-scribe-db/index-db-types.type";
import { getDB } from "./medical-scribe-db/medical-scribe-db.util";

// Maximum recording duration: 2 hours, 59 minutes, 58 seconds
export const MAX_RECORDING_DURATION_MS = (2 * 60 * 60 + 59 * 60 + 58) * 1000; // 10,798,000 ms

export const MIC_THRESHOLD = 0.01;
export const CHUNK_TIMESLICE_MS = 3000;
export const FFT_SIZE = 2048;
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes - sessions older than this are considered stale

const isLocalEnv = viteEnv.VITE_ENV === "local";

export const getPatientIdFromPathname = (pathname: string) => {
  const path = pathname.split("/").filter(Boolean).join("/");

  const patientId =
    path.length >= 10 && path.startsWith("patients/")
      ? Number(path.split("patients/")[1]?.split("/")[0]) || undefined
      : undefined;

  return patientId;
};

export function isAppointmentV2Page(
  flags: Record<string, unknown>,
  pathname: string
): boolean {
  return (
    Boolean(flags[APPOINTMENT_PAGE_REDESIGN]) &&
    /^\/patients\/\d+\/appointments\/\d+\/?$/.test(pathname)
  );
}

export const mergeChunks = (chunks: Blob[] | undefined | null) => {
  let blob: Blob;
  if (chunks && chunks.length > 0) {
    // Filter out empty chunks as they can cause corruption
    const validChunks = chunks.filter((chunk) => chunk && chunk.size > 0);

    if (validChunks.length === 0) {
      if (isLocalEnv) {
        console.warn("mergeChunks: No valid chunks found, creating empty blob");
      } else {
        try {
          datadogLogs.logger.warn("Ambient Scribe", {
            message: "mergeChunks: No valid chunks found, creating empty blob"
          });
        } catch {
          // Silently fail if datadog is not available
        }
      }
      blob = new Blob([], { type: "audio/webm" });
    } else {
      // Ensure chunks are in correct order and create the merged blob
      blob = new Blob(validChunks, { type: "audio/webm" });

      // Validate the merged blob has reasonable size
      if (blob.size === 0) {
        if (isLocalEnv) {
          console.error(
            "mergeChunks: Merged blob is empty despite having valid chunks"
          );
        } else {
          try {
            datadogLogs.logger.error("Ambient Scribe", {
              message:
                "mergeChunks: Merged blob is empty despite having valid chunks"
            });
          } catch {
            // Silently fail if datadog is not available
          }
        }
      } else if (blob.size < 100) {
        if (isLocalEnv) {
          console.warn(
            `mergeChunks: Merged blob is very small (${blob.size} bytes), may be incomplete`
          );
        } else {
          try {
            datadogLogs.logger.warn("Ambient Scribe", {
              message:
                "mergeChunks: Merged blob is very small, may be incomplete",
              blobSize: blob.size
            });
          } catch {
            // Silently fail if datadog is not available
          }
        }
      }
    }
  } else {
    blob = new Blob([], { type: "audio/webm" });
  }

  return blob;
};

export const recordToDataUrl = (chunks: Blob[] | undefined | null) => {
  return URL.createObjectURL(mergeChunks(chunks));
};

export interface SpeakerSetting {
  index: number;
  name: string;
  avatar?: string;
}

export interface FormattedDialog {
  speakerSetting: Record<number, SpeakerSetting>;
  speakersCount: number;
  dialog: DeepGramDialogEntry[];
}

export function groupTranscriptionBySpeakers(
  input: DeepgramInput
): FormattedDialog {
  if (!input) return { speakersCount: 0, dialog: [], speakerSetting: {} };

  const words = input.channels[0]?.alternatives[0]?.words || [];
  const dialog: DeepGramDialogEntry[] = [];

  if (words.length === 0) {
    return { speakersCount: 0, dialog, speakerSetting: {} };
  }

  const speakerSetting: Record<number, SpeakerSetting> = {};

  let currentSpeaker = words[0].speaker;
  let currentText = words[0].punctuated_word;

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    if (word.speaker === currentSpeaker) {
      currentText += ` ${word.punctuated_word}`;
    } else {
      dialog.push({ speaker: currentSpeaker, text: currentText });
      currentSpeaker = word.speaker;
      currentText = word.punctuated_word;
    }
  }

  dialog.push({ speaker: currentSpeaker, text: currentText });

  const speakers = new Set<number>(words.map((word) => word.speaker));

  speakers.values().forEach((speaker) => {
    speakerSetting[speaker] = {
      index: speaker,
      name: `Speaker ${speaker + 1}`,
      avatar: simpleHash(
        dialog
          .filter((dialog) => dialog.speaker === speaker)
          .map((dialog) => dialog.text)
          .join("|")
      )
    };
  });

  return {
    speakersCount: speakers.size,
    speakerSetting,
    dialog
  };
}

export const formattedDialogToText = (dialog: FormattedDialog) => {
  let result: string[] = [];

  dialog.dialog.forEach((entry) => {
    result.push(`Speaker ${Number(entry.speaker) + 1}:`);
    result.push(entry.text ?? "");
    result.push("");
    result.push("");
  });

  return result.join("\r\n");
};

function simpleHash(input: string, length: number = 8): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16).substring(0, length);
}

export const getAmbientScribeStatus = (params: {
  id?: number | string | null;
  status?: number;
  uploadStarted?: number;
  uploadStatus?: string;
  has_transcription?: boolean;
}): number => {
  const { id, uploadStarted, uploadStatus, status, has_transcription } = params;

  // Local-only records (no remote id, or UUID string id)
  if (!id || (!Number(id) && typeof id === "string")) {
    if (uploadStatus === "failed") return AMBIENT_SCRIBE_STATUS_UPLOAD_FAILED;
    return uploadStarted
      ? AMBIENT_SCRIBE_STATUS_UPLOADING
      : AMBIENT_SCRIBE_STATUS_AWAITING_UPLOAD;
  }

  // Remote records: map backend status to FE display status
  switch (status) {
    case 0:
      return AMBIENT_SCRIBE_STATUS_RECORDING;
    case 1:
      return AMBIENT_SCRIBE_STATUS_UPLOADED;
    case 2:
      return AMBIENT_SCRIBE_STATUS_PROCESSING;
    case 3:
      return AMBIENT_SCRIBE_STATUS_TRANSCRIBED;
    case 4:
      return AMBIENT_SCRIBE_STATUS_COMPLETED;
    case 5:
      return AMBIENT_SCRIBE_STATUS_FAILED;
    case 6:
      return AMBIENT_SCRIBE_STATUS_RETRYING;
    case 7:
      return AMBIENT_SCRIBE_STATUS_CANCELLED;
    case 8:
      return AMBIENT_SCRIBE_STATUS_PAUSED;
    default:
      // Fallback when status is undefined/null: use has_transcription as hint
      return has_transcription
        ? AMBIENT_SCRIBE_STATUS_TRANSCRIBED
        : AMBIENT_SCRIBE_STATUS_UPLOADED;
  }
};

export const formatDurationSeconds = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) {
    if (isLocalEnv) {
      console.warn(
        `Invalid duration value: ${seconds}, defaulting to 00:00:00`
      );
    } else {
      try {
        datadogLogs.logger.warn("Ambient Scribe", {
          message: "Invalid duration value, defaulting to 00:00:00",
          seconds
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
    return "00:00";
  }

  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s
    .toString()
    .padStart(2, "0")}`;
};

export interface TranscriptionEstimate {
  estimatedDurationSec: number; // How long transcription will take (seconds)
  estimatedCompletionTime: Date | null; // When it will be completed
}

export function estimateTranscriptionTime(
  audioDurationSec: number,
  transcriptionStartDate?: Date | null
): TranscriptionEstimate {
  // Adjusted speed factor based on observed performance (1 hour ≈ 2.8 minutes)
  // 3600s audio / 168s processing = ~21.43x faster than realtime
  const transcriptionSpeedFactor = 21.43;

  // Fixed overhead estimates (network, S3, DB, etc)
  // Reduced since most of the processing is now accounted for in the speed factor
  const fixedOverheadSec = 1.0;

  // Calculate base transcription time
  const baseTranscriptionSec = audioDurationSec / transcriptionSpeedFactor;

  // Add overhead with 10% buffer for variability
  const estimatedDurationSec = Math.ceil(
    baseTranscriptionSec + fixedOverheadSec * 1.1
  );

  let estimatedCompletionTime: Date | null = null;
  if (transcriptionStartDate) {
    // Calculate completion time
    estimatedCompletionTime = new Date(
      transcriptionStartDate.getTime() + estimatedDurationSec * 1000
    );
  }

  return {
    estimatedDurationSec,
    estimatedCompletionTime
  };
}

export function formatSeconds(seconds: number) {
  // Handle invalid inputs gracefully
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0 minutes"; // Default fallback
  }

  try {
    const duration = intervalToDuration({
      start: 0,
      end: seconds * 1000 + 30000
    });

    return formatDuration(duration, {
      format: ["hours", "minutes"],
      zero: false,
      delimiter: " "
    });
  } catch (error) {
    if (isLocalEnv) {
      console.warn("Error formatting seconds:", error);
    } else {
      try {
        datadogLogs.logger.warn("Ambient Scribe", {
          message: "Error formatting seconds",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
    return "0 minutes"; // Fallback on error
  }
}

export function hhmmssToSeconds(timeStr: string) {
  // Handle invalid inputs gracefully
  if (!timeStr || typeof timeStr !== "string") {
    return 0;
  }

  try {
    const [hours, minutes, seconds] = timeStr.split(":").map(Number);

    // Validate all parts are valid numbers
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      return 0;
    }

    return hours * 3600 + minutes * 60 + seconds;
  } catch (error) {
    if (isLocalEnv) {
      console.warn("Error parsing time string:", error);
    } else {
      try {
        datadogLogs.logger.warn("Ambient Scribe", {
          message: "Error parsing time string",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
    return 0;
  }
}

// Worker-compatible audio handling
export const handleAudioSegmentsForUpload = async (
  segments: { metadata: RecordingData; chunks: Blob[] }[]
): Promise<{ blob: Blob; shouldUpload: boolean; reason: string }> => {
  if (segments.length === 0) {
    throw new Error("No segments to handle");
  }

  if (segments.length === 1) {
    // Single segment - create valid WebM from chunks
    const segment = segments[0];
    const blob = new Blob(segment.chunks, { type: "audio/webm" });
    return {
      blob,
      shouldUpload: true,
      reason: "Single valid segment"
    };
  }

  // Multiple segments - this is problematic for WebM format
  if (isLocalEnv) {
    console.warn(
      `Multiple segments detected (${segments.length}). WebM concatenation may cause corruption.`
    );
  } else {
    try {
      datadogLogs.logger.warn("Ambient Scribe", {
        message:
          "Multiple segments detected, WebM concatenation may cause corruption",
        segmentCount: segments.length
      });
    } catch {
      // Silently fail if datadog is not available
    }
  }

  // Strategy: Use the largest/most recent segment only to avoid corruption
  const largestSegment = segments.reduce((largest, current) => {
    const currentSize = current.chunks.reduce(
      (sum, chunk) => sum + chunk.size,
      0
    );
    const largestSize = largest.chunks.reduce(
      (sum, chunk) => sum + chunk.size,
      0
    );
    return currentSize > largestSize ? current : largest;
  });

  const blob = new Blob(largestSegment.chunks, { type: "audio/webm" });

  if (isLocalEnv) {
    console.log(
      `Using largest segment (${blob.size} bytes) out of ${segments.length} segments to avoid corruption`
    );
  } else {
    try {
      datadogLogs.logger.log("Ambient Scribe", {
        message: "Using largest segment to avoid corruption",
        blobSize: blob.size,
        segmentCount: segments.length
      });
    } catch {
      // Silently fail if datadog is not available
    }
  }

  return {
    blob,
    shouldUpload: true,
    reason: `Largest segment from ${segments.length} segments (avoiding corruption)`
  };
};

// Mark all segments as completed to prevent individual upload
const markSegmentsAsCompleted = async (recordingId: string) => {
  try {
    const db = await getDB();
    const tx = db.transaction("recordings", "readwrite");
    const metadataStore = tx.objectStore("recordings");

    const parentRecord = await metadataStore.get(recordingId);
    if (!parentRecord?.segments) {
      await tx.done;
      return;
    }

    // Mark all segments as completed
    for (const segmentId of parentRecord.segments) {
      const segmentRecord = await metadataStore.get(segmentId);
      if (segmentRecord && segmentRecord.isSegment) {
        segmentRecord.uploadStatus = "completed";
        await metadataStore.put(segmentRecord);
      }
    }

    await tx.done;
    if (isLocalEnv) {
      console.log(
        `Marked ${parentRecord?.segments?.length} segments as completed for recording ${recordingId}`
      );
    } else {
      try {
        datadogLogs.logger.log("Ambient Scribe", {
          message: "Marked segments as completed",
          recordingId,
          segmentCount: parentRecord?.segments?.length
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
  } catch (error) {
    if (isLocalEnv) {
      console.error("Failed to mark segments as completed:", error);
    } else {
      try {
        datadogLogs.logger.error(
          "Ambient Scribe",
          {
            message: "Failed to mark segments as completed",
            recordingId,
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                  }
                : error
          },
          error instanceof Error ? error : undefined
        );
      } catch {
        // Silently fail if datadog is not available
      }
    }
  }
};

// Clean up empty segments for a recording
export const cleanupEmptySegments = async (recordingId: string) => {
  try {
    const db = await getDB();
    const tx = db.transaction(["recordings", "recordings_chunks"], "readwrite");
    const metadataStore = tx.objectStore("recordings");
    const chunksStore = tx.objectStore("recordings_chunks");

    const parentRecord = await metadataStore.get(recordingId);
    if (!parentRecord?.segments) {
      await tx.done;
      return;
    }

    const emptySegments: string[] = [];

    // Check each segment for emptiness
    for (const segmentId of parentRecord.segments) {
      const segmentChunks = await chunksStore.get(segmentId);
      if (!segmentChunks || segmentChunks.chunks.length === 0) {
        emptySegments.push(segmentId);
      }
    }

    // Remove empty segments
    if (emptySegments.length > 0) {
      if (isLocalEnv) {
        console.log(
          `Cleaning up ${emptySegments.length} empty segments:`,
          emptySegments
        );
      } else {
        try {
          datadogLogs.logger.log("Ambient Scribe", {
            message: "Cleaning up empty segments",
            emptySegmentCount: emptySegments.length,
            emptySegmentIds: emptySegments
          });
        } catch {
          // Silently fail if datadog is not available
        }
      }

      // Update parent record to remove empty segment references
      parentRecord.segments = parentRecord.segments.filter(
        (id) => !emptySegments.includes(id)
      );
      await metadataStore.put(parentRecord);

      // Delete empty segment records and chunks
      for (const segmentId of emptySegments) {
        await metadataStore.delete(segmentId);
        await chunksStore.delete(segmentId);
      }
    }

    await tx.done;
  } catch (error) {
    if (isLocalEnv) {
      console.error("Failed to cleanup empty segments:", error);
    } else {
      try {
        datadogLogs.logger.error(
          "Ambient Scribe",
          {
            message: "Failed to cleanup empty segments",
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                  }
                : error
          },
          error instanceof Error ? error : undefined
        );
      } catch {
        // Silently fail if datadog is not available
      }
    }
  }
};

// Get all segments for a recording (including the main recording if it has chunks)
export const getAllRecordingSegments = async (recordingId: string) => {
  const db = await getDB();

  // Use a single transaction for both recordings and chunks for better performance
  const tx = db.transaction(["recordings", "recordings_chunks"], "readonly");
  const metadataStore = tx.objectStore("recordings");
  const chunksStore = tx.objectStore("recordings_chunks");

  try {
    const parentRecord = await metadataStore.get(recordingId);

    if (!parentRecord) {
      throw new Error("Recording not found");
    }

    const allSegments: { metadata: RecordingData; chunks: Blob[] }[] = [];

    // Check if the main recording has chunks (first segment)
    const mainChunks = await chunksStore.get(recordingId);

    if (mainChunks && mainChunks.chunks.length > 0) {
      allSegments.push({
        metadata: { ...parentRecord, segmentIndex: 0 },
        chunks: mainChunks.chunks
      });
    }

    // Get all segment recordings (only non-empty ones)
    if (parentRecord.segments && parentRecord.segments.length > 0) {
      for (const segmentId of parentRecord.segments) {
        const segmentRecord = await metadataStore.get(segmentId);
        const segmentChunks = await chunksStore.get(segmentId);

        if (segmentRecord && segmentChunks && segmentChunks.chunks.length > 0) {
          allSegments.push({
            metadata: segmentRecord,
            chunks: segmentChunks.chunks
          });
        } else if (
          segmentRecord &&
          (!segmentChunks || segmentChunks.chunks.length === 0)
        ) {
          if (isLocalEnv) {
            console.warn(
              `Found empty segment ${segmentId}, skipping from merge`
            );
          } else {
            try {
              datadogLogs.logger.warn("Ambient Scribe", {
                message: "Found empty segment, skipping from merge",
                segmentId
              });
            } catch {
              // Silently fail if datadog is not available
            }
          }
        }
      }
    }

    // Sort segments by their index to maintain order
    allSegments.sort(
      (a, b) => (a.metadata.segmentIndex || 0) - (b.metadata.segmentIndex || 0)
    );

    return allSegments;
  } finally {
    // Ensure transaction is properly closed
    await tx.done;
  }
};

export const saveRecording = async (currentRecordingId: string) => {
  const db = await getDB();

  const metadataTx = db.transaction("recordings", "readwrite");
  const metadataStore = metadataTx.objectStore("recordings");
  const metadata = await metadataStore.get(currentRecordingId);

  if (!metadata) {
    throw new Error("Recording metadata not found");
  }

  // Check if this is a segment or parent recording
  let targetRecordingId = currentRecordingId;
  let targetMetadata = metadata;

  // If this is a segment, we need to work with the parent recording
  if (metadata.isSegment && metadata.parentRecordingId) {
    targetRecordingId = metadata.parentRecordingId;
    const parentMetadata = await metadataStore.get(metadata.parentRecordingId);
    if (parentMetadata) {
      targetMetadata = parentMetadata;
    }
  }

  targetMetadata.uploadStatus = "pending";

  if (targetMetadata.pauseStartTime) {
    const now = Date.now();
    targetMetadata.pausedDuration += now - targetMetadata.pauseStartTime;
    targetMetadata.pauseStartTime = undefined;
  }

  targetMetadata.endTime = Date.now();

  targetMetadata.created_at = new Date().toISOString();

  await metadataStore.put(targetMetadata);
  await metadataTx.done;

  // Clean up any empty segments before processing
  await cleanupEmptySegments(targetRecordingId);

  // Ensure all segments are marked as completed (never upload individually)
  await markSegmentsAsCompleted(targetRecordingId);

  // Get all segments for this recording
  const allSegments = await getAllRecordingSegments(targetRecordingId);

  if (allSegments.length === 0) {
    throw new Error("No recording segments found");
  }

  // Calculate duration: PREFER timestamp-based calculation for accuracy
  // Chunk-based calculation overestimates because first/last chunks are partial
  let calculatedDuration = 0;

  if (targetMetadata.startTime && targetMetadata.endTime) {
    // Use timestamp-based calculation (most accurate)
    const timestampDuration =
      (targetMetadata.endTime - targetMetadata.startTime) / 1000;
    // Subtract paused duration if available
    const actualDuration =
      timestampDuration - (targetMetadata.pausedDuration || 0) / 1000;
    calculatedDuration = Math.max(1, Math.round(actualDuration));
    if (isLocalEnv) {
      console.log(
        `[saveRecording] Using timestamp-based duration: ${calculatedDuration}s ` +
          `(startTime: ${targetMetadata.startTime}, endTime: ${targetMetadata.endTime}, ` +
          `pausedDuration: ${targetMetadata.pausedDuration || 0}ms)`
      );
    } else {
      try {
        datadogLogs.logger.log("Ambient Scribe", {
          message: "[saveRecording] Using timestamp-based duration",
          duration: calculatedDuration,
          startTime: targetMetadata.startTime,
          endTime: targetMetadata.endTime,
          pausedDuration: targetMetadata.pausedDuration || 0
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
  } else {
    // FALLBACK: Use chunk-based calculation (less accurate)
    let totalChunks = 0;
    for (const segment of allSegments) {
      totalChunks += segment.chunks.length;
    }
    // Improved calculation: (N-1) full chunks + partial last chunk
    if (totalChunks === 0) {
      calculatedDuration = 0;
    } else if (totalChunks === 1) {
      calculatedDuration = Math.max(
        1,
        Math.round(CHUNK_TIMESLICE_MS / 2 / 1000)
      );
    } else {
      const fullChunks = totalChunks - 1;
      const estimatedDuration =
        (fullChunks * CHUNK_TIMESLICE_MS + CHUNK_TIMESLICE_MS / 2) / 1000;
      calculatedDuration = Math.max(1, Math.round(estimatedDuration));
    }
    if (isLocalEnv) {
      console.log(
        `[saveRecording] Using chunk-based duration (fallback): ${calculatedDuration}s ` +
          `(chunks: ${totalChunks}, chunkInterval: ${CHUNK_TIMESLICE_MS}ms)`
      );
    } else {
      try {
        datadogLogs.logger.log("Ambient Scribe", {
          message: "[saveRecording] Using chunk-based duration (fallback)",
          duration: calculatedDuration,
          chunks: totalChunks,
          chunkInterval: CHUNK_TIMESLICE_MS
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }
  }

  // Update metadata with calculated duration
  targetMetadata.duration = calculatedDuration;

  // If only one segment, return as-is (no merging needed)
  if (allSegments.length === 1) {
    const segment = allSegments[0];
    return {
      metadata: targetMetadata,
      chunks: segment.chunks
    };
  }

  // Multiple segments detected - avoid corruption by using safe handling
  if (isLocalEnv) {
    console.warn(
      `Multiple segments detected (${allSegments.length}). Using corruption-safe strategy.`
    );
  } else {
    try {
      datadogLogs.logger.warn("Ambient Scribe", {
        message: "Multiple segments detected, using corruption-safe strategy",
        segmentCount: allSegments.length
      });
    } catch {
      // Silently fail if datadog is not available
    }
  }

  try {
    // Use safe handling to avoid WebM corruption
    const { blob, shouldUpload, reason } =
      await handleAudioSegmentsForUpload(allSegments);

    if (!shouldUpload) {
      throw new Error(`Cannot save recording: ${reason}`);
    }

    if (isLocalEnv) {
      console.log(`Saving recording using safe strategy: ${reason}`);
    } else {
      try {
        datadogLogs.logger.log("Ambient Scribe", {
          message: "Saving recording using safe strategy",
          reason
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }

    return {
      metadata: { ...targetMetadata, duration: calculatedDuration },
      chunks: [blob] // Single safe blob
    };
  } catch (error) {
    if (isLocalEnv) {
      console.error(
        "Safe handling failed, this may result in backend errors:",
        error
      );
    } else {
      try {
        datadogLogs.logger.error(
          "Ambient Scribe",
          {
            message: "Safe handling failed, this may result in backend errors",
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                  }
                : error
          },
          error instanceof Error ? error : undefined
        );
      } catch {
        // Silently fail if datadog is not available
      }
    }

    // Final fallback: use largest segment only
    const largestSegment = allSegments.reduce((largest, current) => {
      const currentSize = current.chunks.reduce(
        (sum, chunk) => sum + chunk.size,
        0
      );
      const largestSize = largest.chunks.reduce(
        (sum, chunk) => sum + chunk.size,
        0
      );
      return currentSize > largestSize ? current : largest;
    });

    // Recalculate duration based on the largest segment chunks only
    // Use improved calculation that accounts for partial chunks
    let largestSegmentDuration = 0;
    if (largestSegment.chunks.length === 0) {
      largestSegmentDuration = 0;
    } else if (largestSegment.chunks.length === 1) {
      largestSegmentDuration = Math.max(
        1,
        Math.round(CHUNK_TIMESLICE_MS / 2 / 1000)
      );
    } else {
      const fullChunks = largestSegment.chunks.length - 1;
      const estimatedDuration =
        (fullChunks * CHUNK_TIMESLICE_MS + CHUNK_TIMESLICE_MS / 2) / 1000;
      largestSegmentDuration = Math.max(1, Math.round(estimatedDuration));
    }

    if (isLocalEnv) {
      console.warn(
        `Using only the largest segment to avoid corruption (${largestSegment.chunks.length} chunks, ${largestSegmentDuration}s)`
      );
    } else {
      try {
        datadogLogs.logger.warn("Ambient Scribe", {
          message: "Using only the largest segment to avoid corruption",
          chunkCount: largestSegment.chunks.length,
          duration: largestSegmentDuration
        });
      } catch {
        // Silently fail if datadog is not available
      }
    }

    return {
      metadata: {
        ...targetMetadata,
        duration: largestSegmentDuration
      },
      chunks: largestSegment.chunks,
      audioUrl: URL.createObjectURL(
        new Blob(largestSegment.chunks || [], { type: "audio/webm" })
      )
    };
  }
};
